import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { useToastContext } from '../../../../contexts/toast'
import { getHandleableError } from '../../../../utils/errors/isXiaError'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../utils/helper/validateFormikFields'
import { DefaultConfig } from '../../../ui-config/data'
import {
  DataForConfirmationDto,
  DataForConfirmationPatchDto,
} from '../../data/model'
import { DataForDeliveryForecastDespatchAdviceView } from './DataForDeliveryForecastDespatchAdvice.view'

type Props = {
  data: DataForConfirmationDto
  config: DefaultConfig
  onSubmit: (data: DataForConfirmationPatchDto) => Promise<void>
  isLoading: boolean
}

interface FormValues extends DataForConfirmationDto {}
export type FormFieldName = keyof DataForConfirmationDto

export const DataForDeliveryForecastDespatchAdviceForm: React.FC<Props> = ({
  data,
  config,
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation()

  const { showToast } = useToastContext()

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { ...data },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'deliveryNoteNumber',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.deliveryNoteNumber,
        },
        {
          fieldName: 'deliveryDate',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.deliveryDate,
        },
        {
          fieldName: 'termsOfDeliveryCode',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.termsOfDeliveryCode,
        },
        {
          fieldName: 'confirmationGrossWeight',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.confirmationGrossWeight,
        },
        {
          fieldName: 'confirmationGrossWeightUnit',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice
              .confirmationGrossWeightUnit,
        },
        {
          fieldName: 'confirmationNumberOfUnits',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.confirmationNumberOfUnits,
        },
        {
          fieldName: 'gln',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.gln,
        },
        {
          fieldName: 'company',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.company,
        },
        {
          fieldName: 'department',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.department,
        },
        {
          fieldName: 'name',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.name,
        },
        {
          fieldName: 'phone',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.phone,
        },
        {
          fieldName: 'email',
          config:
            config.deliveryForecastDespatchAdvice
              .dataForDeliveryForecastDespatchAdvice.email,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: data => {
      try {
        // send data in DespatchAdviceData
        onSubmit(data)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'deliveryForecastDespatchAdvice.dataForDeliveryForecastDespatchAdvice.save.messages.success.summary',
          ),
          detail: t(
            'deliveryForecastDespatchAdvice.dataForDeliveryForecastDespatchAdvice.save.messages.success.detail',
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'deliveryForecastDespatchAdvice.dataForDeliveryForecastDespatchAdvice.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  return (
    <DataForDeliveryForecastDespatchAdviceView
      data={data}
      config={config}
      formik={formik}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
      isLoading={isLoading}
    />
  )
}
